/**
 * SetupBand (BLU-1376, handover 4.1): the setup page's sticky progress band.
 *
 * Rendered as the children of the shared `PageActionBar`, so it pins under
 * TopHeader and publishes `--page-action-bar-height` (the rail pins under it
 * by reading that variable). `PageActionBar.tsx` is not edited: its surface
 * carries a translucent `bg-background/85` fill, a
 * `supports-[backdrop-filter]:bg-background/65` fill and `backdrop-blur`, and
 * `cn()` only replaces a class with the same variant, so
 * the className below overrides the `supports-[backdrop-filter]:` fill and the
 * blur explicitly; a bare `bg-primary-strong` would paint translucent white.
 * The bar wraps its children in a `nav` named by `ariaLabel`, so the page's
 * only `h1` sits inside that landmark (accepted: the band is the page chrome).
 *
 * With `progress` (the step flow) it shows the "N of M done" count, a refresh
 * button and an 8px progress bar whose fill eases over 300ms (DESIGN 5). The
 * refresh spinner shows only while `refreshing` is true; the page sets that for
 * a MANUAL refresh only, never for background polls. Without `progress` (the
 * "How do you print?" question) none of the three render.
 */
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { PageActionBar } from '@/components/layout/PageActionBar';
import { cn } from '@/lib/utils';
import type { PrintMode } from '@threadheads/shared';

interface SetupBandProps {
  progress?: { done: number; total: number };
  refreshing?: boolean;
  onRefresh?: () => void;
  printMode?: PrintMode | null;
  onChangePrintMode?: () => void;
  /** The caller may change the print-mode answer (Coordinator and up). */
  canChange?: boolean;
}

/** White focus ring: the default brand ring vanishes on the purple band. */
const BAND_FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-strong';

export function SetupBand({
  progress,
  refreshing = false,
  onRefresh,
  printMode,
  onChangePrintMode,
  canChange = false,
}: SetupBandProps) {
  const { t } = useTranslation('onboarding');
  const total = progress ? Math.max(progress.total, 0) : 0;
  const done = progress ? Math.min(Math.max(progress.done, 0), total) : 0;
  const percent = total > 0 ? (done / total) * 100 : 0;
  const countText = progress ? t('band.count', { done, total }) : '';

  return (
    <PageActionBar
      ariaLabel={t('band.title')}
      className="bg-primary-strong text-white backdrop-blur-none supports-[backdrop-filter]:bg-primary-strong"
    >
      <div className="flex flex-col gap-3 pb-0.5">
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-7 text-white">{t('band.title')}</h1>
            <p className="text-sm leading-5 text-white/80">{t('band.subtitle')}</p>
          </div>
          {progress && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm font-medium tabular-nums text-white">{countText}</span>
              {onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={refreshing}
                  aria-label={t('band.refresh')}
                  aria-busy={refreshing || undefined}
                  className={cn(
                    'grid h-8 w-8 place-items-center rounded-md text-white/85 hover:bg-white/10 hover:text-white disabled:cursor-default',
                    'transition-colors duration-150 ease-out motion-reduce:transition-none',
                    BAND_FOCUS,
                  )}
                >
                  <RefreshCw
                    className={cn('h-4 w-4', refreshing && 'animate-spin motion-reduce:animate-none')}
                    aria-hidden="true"
                    data-spinning={refreshing || undefined}
                  />
                </button>
              )}
            </div>
          )}
        </div>

        {progress && (
          <div
            role="progressbar"
            aria-label={t('band.progressLabel')}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
            aria-valuetext={countText}
            className="h-2 w-full overflow-hidden rounded-full bg-white/20"
          >
            <div
              className="h-full rounded-full bg-white transition-[width] duration-300 ease-out-expo motion-reduce:transition-none"
              style={{ width: `${percent}%` }}
            />
          </div>
        )}

        {printMode && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex h-7 items-center rounded-md bg-white/10 px-2.5 text-xs font-medium text-white ring-1 ring-inset ring-white/25">
              {t('band.printMode', {
                answer: printMode === 'client' ? t('band.printModeClient') : t('band.printModeSelf'),
              })}
            </span>
            {canChange && onChangePrintMode && (
              <button
                type="button"
                onClick={onChangePrintMode}
                className={cn(
                  'rounded-sm px-1 text-xs font-medium text-white underline decoration-white/50 underline-offset-4 hover:decoration-white',
                  'transition-colors duration-150 ease-out motion-reduce:transition-none',
                  BAND_FOCUS,
                )}
              >
                {t('band.change')}
              </button>
            )}
          </div>
        )}
      </div>
    </PageActionBar>
  );
}
